import {
  Avatar,
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Select,
  Text,
  VStack,
  createListCollection,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { Users } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "../../trpc/client";
import { useMerchant } from "../../hooks/useMerchant";
import { useFetchUser } from "../../hooks/useFetchUser";
import { toaster } from "../ui/toaster";

const USERS_LIST_HEIGHT = "300px";

const inviteRoleCollection = createListCollection({
  items: [
    { label: "Solo lectura", value: "viewer" },
    { label: "Editor", value: "editor" },
  ],
});

type EditableRole = "editor" | "viewer";

const UsersTab = (): JSX.Element => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { merchant } = useMerchant();
  const { user: currentUser } = useFetchUser();

  const [inviteData, setInviteData] = useState<{
    email: string;
    displayName: string;
    role: EditableRole;
  }>({ email: "", displayName: "", role: "viewer" });

  const { data: users, isLoading } = useQuery({
    ...trpc.users.listByMerchant.queryOptions({
      rentalsMerchantId: merchant?.id ?? "",
    }),
    enabled: !!merchant?.id,
  });

  const merchantUsers = useMemo(() => {
    if (!users) return [];
    const merchantId = merchant?.id ?? "";
    const isAdmin = (user: (typeof users)[number]) =>
      user.merchants?.[merchantId]?.roles?.includes("admin") ?? false;

    return [...users].sort((a, b) => {
      const adminDiff = Number(isAdmin(b)) - Number(isAdmin(a));
      if (adminDiff !== 0) return adminDiff;
      if (a.email === currentUser?.email) return -1;
      if (b.email === currentUser?.email) return 1;
      return (a.displayName || a.email).localeCompare(
        b.displayName || b.email,
        "es"
      );
    });
  }, [users, currentUser?.email, merchant?.id]);

  const membershipRoles = (user: (typeof merchantUsers)[number]) =>
    user.merchants?.[merchant?.id ?? ""]?.roles ?? [];

  const editableRole = (user: (typeof merchantUsers)[number]): EditableRole =>
    membershipRoles(user).includes("editor") ? "editor" : "viewer";

  const isMerchantAdmin = (user: (typeof merchantUsers)[number]) =>
    membershipRoles(user).includes("admin");

  const invalidateUsers = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.users.listByMerchant.queryKey({
        rentalsMerchantId: merchant?.id ?? "",
      }),
    });

  const notifyError = (description: string) =>
    toaster.create({
      title: "Error",
      description,
      type: "error",
      duration: 5000,
      meta: { closable: true },
    });

  const { mutateAsync: invite, isPending: isInviting } = useMutation({
    ...trpc.users.invite.mutationOptions(),
    onSuccess: () => {
      invalidateUsers();
      setInviteData({ email: "", displayName: "", role: "viewer" });
      toaster.create({
        title: "Usuario invitado",
        description: "Le enviamos un correo con el acceso al portal.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
    },
    onError: () => notifyError("Hubo un error al invitar al usuario."),
  });

  const { mutateAsync: removeFromMerchant } = useMutation({
    ...trpc.users.removeFromMerchant.mutationOptions(),
    onSuccess: invalidateUsers,
    onError: () => notifyError("Hubo un error al remover el usuario."),
  });

  const { mutateAsync: updateRole, isPending: isUpdatingRole } = useMutation({
    ...trpc.users.updateRole.mutationOptions(),
    onSuccess: invalidateUsers,
    onError: () => notifyError("Hubo un error al actualizar el rol."),
  });

  const isInviteValid = () =>
    /\S+@\S+\.\S+/.test(inviteData.email) &&
    inviteData.displayName.trim().length > 0;

  return (
    <VStack gap={4} align="stretch">
      <Box>
        <Text fontWeight={600}>Usuarios</Text>
        <Text fontSize="sm" color="gray.500">
          Invita a tu equipo y gestiona quién tiene acceso al portal
        </Text>
      </Box>

      <Box>
        <HStack gap={2} align="center">
          <Input
            flex="1"
            size="sm"
            value={inviteData.displayName}
            onChange={(e) =>
              setInviteData({ ...inviteData, displayName: e.target.value })
            }
            placeholder="Nombre"
          />
          <Input
            flex="1.4"
            size="sm"
            value={inviteData.email}
            onChange={(e) =>
              setInviteData({ ...inviteData, email: e.target.value })
            }
            type="email"
            placeholder="correo@comercio.com"
          />
          <Select.Root
            collection={inviteRoleCollection}
            size="sm"
            width="150px"
            flexShrink={0}
            value={[inviteData.role]}
            onValueChange={(details) => {
              const role = details.value[0] as EditableRole | undefined;
              if (role) setInviteData({ ...inviteData, role });
            }}
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText placeholder="Rol" />
              </Select.Trigger>
              <Select.IndicatorGroup>
                <Select.Indicator />
              </Select.IndicatorGroup>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                {inviteRoleCollection.items.map((item) => (
                  <Select.Item key={item.value} item={item}>
                    {item.label}
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
          <Button
            size="sm"
            loading={isInviting}
            disabled={!isInviteValid()}
            onClick={() =>
              invite({ rentalsMerchantId: merchant!.id, ...inviteData })
            }
            flexShrink={0}
          >
            Invitar
          </Button>
        </HStack>
        <Text fontSize="xs" color="gray.500" mt={1.5}>
          {inviteData.role === "viewer"
            ? "Recibirá un correo con acceso de solo lectura al portal"
            : "Recibirá un correo con acceso al portal como editor"}
        </Text>
      </Box>

      <Box
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        overflow="hidden"
      >
        <HStack
          justify="space-between"
          align="center"
          px={3}
          py={2}
          borderBottomWidth="1px"
          borderColor="gray.200"
          bg="gray.50"
        >
          <Text fontSize="xs" fontWeight={600} color="gray.600">
            Equipo
          </Text>
          <Text fontSize="xs" color="gray.500">
            {isLoading
              ? "…"
              : `${merchantUsers.length} usuario${
                  merchantUsers.length === 1 ? "" : "s"
                }`}
          </Text>
        </HStack>

        <Box height={USERS_LIST_HEIGHT} overflowY="auto">
          {isLoading ? (
            <VStack justify="center" height="100%" gap={1}>
              <Text fontSize="sm" color="gray.500">
                Cargando usuarios...
              </Text>
            </VStack>
          ) : !merchantUsers.length ? (
            <VStack justify="center" height="100%" gap={2} px={4}>
              <Box
                bg="gray.100"
                borderRadius="full"
                p={3}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Users size={20} color="#6a7282" />
              </Box>
              <Text fontWeight={600} fontSize="sm">
                Sin usuarios todavía
              </Text>
              <Text
                fontSize="xs"
                color="gray.500"
                textAlign="center"
                maxWidth="260px"
              >
                Invita a alguien arriba para empezar a armar el equipo.
              </Text>
            </VStack>
          ) : (
            <VStack align="stretch" gap={0}>
              {merchantUsers.map((user, index) => {
                const isCurrent = user.email === currentUser?.email;
                const inactive = user.status === "inactive";
                const role = editableRole(user);
                const showAdminBadge = isMerchantAdmin(user);

                return (
                  <HStack
                    key={user.email}
                    gap={3}
                    px={3}
                    py={2.5}
                    opacity={inactive ? 0.55 : 1}
                    bg={isCurrent ? "purple.50" : "white"}
                    borderBottomWidth={
                      index < merchantUsers.length - 1 ? "1px" : "0"
                    }
                    borderColor="gray.100"
                    _hover={{ bg: isCurrent ? "purple.50" : "gray.50" }}
                    transition="background 0.12s ease"
                  >
                    <Avatar.Root size="sm" colorPalette="palomma">
                      <Avatar.Fallback name={user.displayName || user.email} />
                    </Avatar.Root>
                    <Box minWidth={0} flex="1">
                      <HStack gap={1.5} minWidth={0}>
                        <Text fontSize="sm" fontWeight={600} truncate>
                          {user.displayName || user.email}
                        </Text>
                        {isCurrent && (
                          <Text
                            fontSize="xs"
                            color="purple.600"
                            fontWeight={500}
                            flexShrink={0}
                          >
                            Tú
                          </Text>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="gray.500" truncate>
                        {user.email}
                      </Text>
                    </Box>
                    <HStack flexShrink={0} gap={2} align="center">
                      <Select.Root
                        collection={inviteRoleCollection}
                        size="xs"
                        width="130px"
                        disabled={isUpdatingRole}
                        value={[role]}
                        onValueChange={(details) => {
                          const next = details.value[0] as
                            | EditableRole
                            | undefined;
                          if (!next || next === role) return;
                          updateRole({
                            rentalsMerchantId: merchant!.id,
                            email: user.email,
                            role: next,
                          });
                        }}
                      >
                        <Select.Control>
                          <Select.Trigger>
                            <Select.ValueText placeholder="Rol" />
                          </Select.Trigger>
                          <Select.IndicatorGroup>
                            <Select.Indicator />
                          </Select.IndicatorGroup>
                        </Select.Control>
                        <Select.Positioner>
                          <Select.Content>
                            {inviteRoleCollection.items.map((item) => (
                              <Select.Item key={item.value} item={item}>
                                {item.label}
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Select.Root>
                      <Box width="68px" flexShrink={0}>
                        <Badge
                          colorPalette={inactive ? "red" : "green"}
                          variant="subtle"
                        >
                          {inactive ? "Inactivo" : "Activo"}
                        </Badge>
                      </Box>
                      <Box width="72px" flexShrink={0}>
                        {showAdminBadge ? (
                          <Badge colorPalette="purple" variant="subtle">
                            Admin
                          </Badge>
                        ) : !isCurrent ? (
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="red"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `¿Remover a ${user.email} de este comercio?`
                                )
                              )
                                removeFromMerchant({
                                  rentalsMerchantId: merchant!.id,
                                  email: user.email,
                                });
                            }}
                          >
                            Remover
                          </Button>
                        ) : null}
                      </Box>
                    </HStack>
                  </HStack>
                );
              })}
            </VStack>
          )}
        </Box>
      </Box>
    </VStack>
  );
};

export default UsersTab;
